import type AccessLevel from '@/SDK/AccessLevel'

type PermissionLists = {
  permissions: {
    permissionName: string
    accessLevel: AccessLevel
  }[]
}

type RLSValueLists = {
  rowLevelSecurities: {
    value: string
    accessLevel: AccessLevel
  }[]
}

export default class PermissionChecker {
  private readonly applicationName: string
  private readonly url: URL
  private readonly tokenProvider: (() => Promise<string | null>) | null = null
  private username: string | null = null

  /**
   *
   * @param applicationName
   * @param url
   * @param tokenProvider Promise<string | null>
   */
  constructor(applicationName: string, url: string, tokenProvider?: () => Promise<string | null>) {
    this.applicationName = applicationName
    this.url = new URL('public', url)

    if (tokenProvider) {
      this.tokenProvider = tokenProvider
    }
  }

  private async fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    if (!this.username) throw new Error('Permission checks need a user to work')

    const headers = new Headers({
      'application-name': this.applicationName,
      username: this.username,
      ...init?.headers
    })

    if (this.tokenProvider) {
      const token = await this.tokenProvider()

      if (token) headers.set('Authorization', `Bearer ${token}`)
    }

    init = { ...init, headers }

    return fetch(input, init)
  }

  setUser(username: string) {
    this.username = username
  }

  removeUser() {
    this.username = null
  }

  /**
   * Get all permissions that this user has within the application
   *
   * https://rbac-backend.alphareds.com/reference#tag/public/GET/public/permissions
   */
  async getPermissions() {
    const res = await this.fetch(`${this.url}/permissions`, {
      method: 'GET'
    })

    if (!res.ok) {
      throw new Error('An error occurred!')
    }

    return (await res.json()) as PermissionLists
  }

  /**
   * Get all permissions that this user has within the application, with greater or equal specified access level
   *
   * https://rbac-backend.alphareds.com/reference#tag/public/GET/public/permissions/list/{minAccessLevel}
   * @param minAccessLevel
   */
  async getPermissionsWithGoEAccessLevel(minAccessLevel: AccessLevel) {
    const res = await this.fetch(`${this.url}/permissions/list/${minAccessLevel}`, {
      method: 'GET'
    })

    if (!res.ok) {
      throw new Error('An error occurred!')
    }

    return (await res.json()) as PermissionLists
  }

  /**
   * Check for permission with this name and specified minimum access level
   *
   * https://rbac-backend.alphareds.com/reference#tag/public/GET/public/permissions/{permissionName}/{minAccessLevel}
   * @param permissionName
   * @param minAccessLevel
   */
  async hasPermission(permissionName: string, minAccessLevel: AccessLevel) {
    const res = await this.fetch(`${this.url}/permissions/${permissionName}/${minAccessLevel}`, {
      method: 'GET'
    })

    if (!res.ok) {
      throw new Error('An error occurred!')
    }

    return (await res.json()) as { hasPermission: boolean }
  }

  /**
   * Get the access level of the specified permission name
   *
   * https://rbac-backend.alphareds.com/reference#tag/public/GET/public/permissions/{permissionName}
   * @param permissionName
   */
  async getPermissionAccessLevel(permissionName: string) {
    const res = await this.fetch(`${this.url}/permissions/${permissionName}`, {
      method: 'GET'
    })

    if (!res.ok) {
      throw new Error('An error occurred!')
    }

    return (await res.json()) as { accessLevel: AccessLevel }
  }

  /**
   * Get all rls value that this user has within the application, with greater or equal specified access level
   *
   * https://rbac-backend.alphareds.com/reference#tag/public/GET/public/row-level-securities/list/{entity}/{minAccessLevel}
   * @param entity
   * @param minAccessLevel
   */
  async getRLSWithGoEAccessLevel(entity: string, minAccessLevel: AccessLevel) {
    const res = await this.fetch(`${this.url}/row-level-securities/list/${entity}/${minAccessLevel}`, {
      method: 'GET'
    })

    if (!res.ok) {
      throw new Error('An error occurred!')
    }

    return (await res.json()) as RLSValueLists
  }

  /**
   * ROW LEVEL SECURITIES
   */

  /**
   * Check for rls with this entity, value and specified minimum access level
   *
   * https://rbac-backend.alphareds.com/reference#tag/public/GET/public/row-level-securities/{entity}/{value}/{minAccessLevel}
   * @param entity
   * @param value
   * @param minAccessLevel
   */
  async hasRLS(entity: string, value: string, minAccessLevel: AccessLevel) {
    const res = await this.fetch(`${this.url}/row-level-securities/${entity}/${value}/${minAccessLevel}`, {
      method: 'GET'
    })

    if (!res.ok) {
      throw new Error('An error occurred!')
    }

    return (await res.json()) as { hasRowLevelSecurity: boolean }
  }

  /**
   * Get the access level of the specified rls entity and value
   *
   * https://rbac-backend.alphareds.com/reference#tag/public/GET/public/row-level-securities/{entity}/{value}
   * @param entity
   * @param value
   */
  async getRLSAccessLevel(entity: string, value: string) {
    const res = await this.fetch(`${this.url}/row-level-securities/${entity}/${value}`, {
      method: 'GET'
    })

    if (!res.ok) {
      throw new Error('An error occurred!')
    }

    return (await res.json()) as { accessLevel: AccessLevel }
  }

  /**
   * Get all rls value of this entity
   *
   * https://rbac-backend.alphareds.com/reference#tag/public/GET/public/row-level-securities/{entity}
   */
  async getRLS(entity: string) {
    const res = await this.fetch(`${this.url}/row-level-securities/${entity}`, {
      method: 'GET'
    })

    if (!res.ok) {
      throw new Error('An error occurred!')
    }

    return (await res.json()) as RLSValueLists
  }
}
